/**
 * Canonical Floor Sheet document geometry — paper form layout.
 * Keep in sync with `server/src/pdf/floorSheetLayout.ts`.
 */

export const FS_PAGE = {
  width: 1000,
  height: 707,
  paddingTop: 10,
  paddingBottom: 18,
  paddingH: 10
} as const

export const FS_CONTENT_WIDTH = FS_PAGE.width - FS_PAGE.paddingH * 2
export const FS_COL_GAP = 6
export const FS_COL_LEFT = Math.floor(FS_CONTENT_WIDTH * 0.48)
export const FS_COL_RIGHT = FS_CONTENT_WIDTH - FS_COL_GAP - FS_COL_LEFT

export const FS_SECTION_HEADER_HEIGHT = 14
export const FS_ROW_HEIGHT = 12
export const FS_CLOCK_ROW_HEIGHT = 11
export const FS_LOGO_W = 140
export const FS_LOGO_H = 56
/** Match PQR SharedTopBand identifier badge minWidth (pdfPrimitives.topInfoBox). */
export const FS_TOP_INFO_W = 160

export const FS_PASS_COL = 54
export const FS_EXTRA_COL = 26
export const FS_KV_LABEL_W = 138
export const FS_KV_ROW_HEIGHT = 14
export const FS_BEVEL_LABEL_COL = 96
export const FS_BEVEL_PUP_COL = 46

export const FS_BEVEL_IMG_W = 150
export const FS_BEVEL_IMG_H = 100
export const FS_BEVEL_BOX_H = 100

/**
 * Cap KV label width so narrow Target Values columns keep usable value space
 * (PDF content width is tighter than the 1000px preview canvas).
 */
export function kvLabelWidth(rowWidth: number): number {
  return Math.min(FS_KV_LABEL_W, Math.floor(rowWidth * 0.45))
}

/** Symmetrical IWM grid: CCW label | CCW val | Head | CW val | CW label */
export const IWM_PARAM_FIELDS = ['amps', 'volts', 'wfs', 'gas'] as const
export type IwmParamField = (typeof IWM_PARAM_FIELDS)[number]

export const IWM_PARAM_LABELS: Record<IwmParamField, string> = {
  amps: 'Amps',
  volts: 'Volts',
  wfs: 'WFS (in/min)',
  gas: 'Gas (CFH)'
}

export function iwmColumnWidths(tableWidth: number) {
  // Head needs ~14–16% so “Head Number” is not truncated; value cols stay equal.
  const head = Math.max(70, Math.floor(tableWidth * 0.15))
  const ccwLabel = Math.floor(tableWidth * 0.17)
  const ccwVal = Math.floor(tableWidth * 0.18)
  const cwVal = Math.floor(tableWidth * 0.18)
  const cwLabel = tableWidth - ccwLabel - ccwVal - head - cwVal
  return { ccwLabel, ccwVal, head, cwVal, cwLabel }
}

/** Paper form row order — Root before Hot. */
export const PREHEAT_TEMPLATE_ROWS = [
  'Root',
  'Hot',
  'Fill 1',
  'Fill 2',
  'Fill 3',
  'Fill 4',
  'Fill 5',
  'Fill 6',
  'Fill 7',
  'Fill 8',
  'Fill 9',
  'Strip',
  'Last Fill',
  'Cap 1',
  'Cap 2'
] as const

export const DEPOSITION_TEMPLATE_ROWS = [
  'Root',
  'Hot',
  'Fill 1',
  'Fill 2',
  'Fill 3',
  'Fill 4',
  'Fill 5',
  'Fill 6',
  'Fill 7',
  'Fill 8',
  'Fill 9',
  'Last Fill'
] as const

export const BEVEL_MEASUREMENT_ROWS = [
  'Wall Thickness',
  'Shelf Height',
  'Offset',
  'Land Thickness'
] as const

export function clockColWidth(tableWidth: number, extraCount: number, passCol = FS_PASS_COL): number {
  return Math.floor((tableWidth - passCol - FS_EXTRA_COL * extraCount) / 4)
}

export function bevelRowColWidth(tableWidth: number): number {
  return Math.floor((tableWidth - FS_BEVEL_LABEL_COL - FS_BEVEL_PUP_COL) / 4)
}

function emptyClockMap(locations: string[]): Record<string, string> {
  return Object.fromEntries(locations.map((loc) => [loc, '—']))
}

function rowHasClockData(values: Record<string, string>): boolean {
  return Object.values(values).some((v) => v && v !== '—')
}

export function mergePreheatTemplateRows(
  template: readonly string[],
  dataRows: Array<{
    pass: string
    values: Record<string, string>
    min: string
    max: string
  }>,
  locations: string[]
): Array<{
  label: string
  values: Record<string, string>
  extra: string[]
  unused: boolean
}> {
  const byPass = new Map(dataRows.map((r) => [r.pass, r]))
  return template.map((pass) => {
    const row = byPass.get(pass)
    if (!row) {
      return {
        label: pass,
        values: emptyClockMap(locations),
        extra: ['—', '—'],
        unused: true
      }
    }
    const hasData =
      rowHasClockData(row.values) ||
      (row.min && row.min !== '—') ||
      (row.max && row.max !== '—')
    return {
      label: pass,
      values: row.values,
      extra: [row.min || '—', row.max || '—'],
      unused: !hasData
    }
  })
}

export function mergeDepositionTemplateRows(
  template: readonly string[],
  dataRows: Array<{ pass: string; values: Record<string, string>; avg: string }>,
  locations: string[]
): Array<{
  label: string
  values: Record<string, string>
  extra: string[]
  unused: boolean
}> {
  const byPass = new Map(dataRows.map((r) => [r.pass, r]))
  return template.map((pass) => {
    const row = byPass.get(pass)
    if (!row) {
      return {
        label: pass,
        values: emptyClockMap(locations),
        extra: ['—'],
        unused: true
      }
    }
    const hasData = rowHasClockData(row.values) || (row.avg && row.avg !== '—')
    return {
      label: pass,
      values: row.values,
      extra: [row.avg || '—'],
      unused: !hasData
    }
  })
}

export function headCellUsed(cell: {
  amps: string
  volts: string
  gas: string
  wfs: string
}): boolean {
  return [cell.amps, cell.volts, cell.gas, cell.wfs].some((v) => v && v !== '—')
}

/** Optional fill-template padding that collapses when unused. */
function isCollapsibleUnusedPass(label: string): boolean {
  return /^Fill [3-9]$/.test(label) || label === 'Strip' || label === 'Last Fill'
}

/**
 * Drop trailing unused pass rows after the last used pass, and hide unused
 * Fill 3–9 / Strip / Last Fill padding. Root and Fill 1 always remain.
 */
export function visiblePassRows<T extends { label: string; unused: boolean }>(
  rows: T[],
  options?: { alwaysShow?: readonly string[] }
): T[] {
  const alwaysShow = options?.alwaysShow ?? ['Root', 'Fill 1']
  let lastUsed = -1
  for (let i = 0; i < rows.length; i++) {
    if (!rows[i].unused) lastUsed = i
  }
  if (lastUsed < 0) {
    return rows.filter((r) => alwaysShow.includes(r.label))
  }
  return rows.slice(0, lastUsed + 1).filter((r) => {
    if (!r.unused) return true
    if (alwaysShow.includes(r.label)) return true
    if (isCollapsibleUnusedPass(r.label)) return false
    return true
  })
}

export type IwmHeadLike = {
  head: number | string
  cw: { amps: string; volts: string; gas: string; wfs: string }
  ccw: { amps: string; volts: string; gas: string; wfs: string }
}

/** Hide unused IWM head blocks; keep one empty block when none have data. */
export function visibleIwmHeads<T extends IwmHeadLike>(heads: T[]): T[] {
  const used = heads.filter((h) => headCellUsed(h.cw) || headCellUsed(h.ccw))
  if (used.length > 0) return used
  return heads.length > 0 ? [heads[0]] : []
}
